import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import './RegistrationForm.css';
import { getStoredInvite, clearStoredInvite } from '../utils/invite';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

// Autocomplete component for nonprofit names
const AutocompleteInput = React.memo(({ question, questionAnswer, onValueChange, nonprofitData }) => {
    const [inputValue, setInputValue] = useState(questionAnswer?.value || '');
    const [filteredOptions, setFilteredOptions] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [validationError, setValidationError] = useState('');
    const isSelectingFromDropdown = React.useRef(false);

    // Update input value when questionAnswer changes
    useEffect(() => {
        setInputValue(questionAnswer?.value || '');
    }, [questionAnswer?.value]);

    const validateNonprofitName = useCallback((companyName) => {
        if (!companyName || companyName.trim() === '') {
            return { isValid: false, message: 'Organization name is required' };
        }

        const trimmedName = companyName.trim();
        const isInList = nonprofitData.some(org =>
            org.toLowerCase() === trimmedName.toLowerCase()
        );

        if (!isInList) {
            return {
                isValid: false,
                message: 'This organization is not currently in our list of approved nonprofits. Please contact ',
                emailLink: 'service@funds4charity.org',
                messageEnd: ' for assistance.'
            };
        }

        return { isValid: true, message: '' };
    }, [nonprofitData]);

    const handleInputChange = useCallback((e) => {
        const value = e.target.value;
        setInputValue(value);
        onValueChange(question.EventQuestionId, value);

        // Clear any previous validation error while typing
        setValidationError('');

        if (value.length > 0) {
            const filtered = nonprofitData.filter(org =>
                org.toLowerCase().includes(value.toLowerCase())
            );
            setFilteredOptions(filtered);
            setShowDropdown(filtered.length > 0);
            setHighlightedIndex(-1);
        } else {
            setFilteredOptions([]);
            setShowDropdown(false);
        }
    }, [question.EventQuestionId, onValueChange, nonprofitData]);

    const handleOptionClick = useCallback((option) => {
        isSelectingFromDropdown.current = true;
        setInputValue(option);
        onValueChange(question.EventQuestionId, option);
        setShowDropdown(false);
        setFilteredOptions([]);

        // Clear validation error since dropdown options are all valid
        setValidationError('');
        
        // Reset the flag after a short delay
        setTimeout(() => {
            isSelectingFromDropdown.current = false;
        }, 200);
    }, [question.EventQuestionId, onValueChange]);

    const handleKeyDown = useCallback((e) => {
        if (!showDropdown) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev =>
                    prev < filteredOptions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0) {
                    handleOptionClick(filteredOptions[highlightedIndex]);
                }
                break;
            case 'Escape':
                setShowDropdown(false);
                setHighlightedIndex(-1);
                break;
            default:
                break;
        }
    }, [showDropdown, filteredOptions, highlightedIndex, handleOptionClick]);

    const handleBlur = useCallback(() => {
        // Delay hiding dropdown to allow for option clicks
        setTimeout(() => {
            setShowDropdown(false);
            
            // Skip validation if user is selecting from dropdown
            if (isSelectingFromDropdown.current) {
                return;
            }
            
            // Validate the input after blur only if not empty
            if (inputValue && inputValue.trim() !== '') {
                const validation = validateNonprofitName(inputValue);
                setValidationError(validation.isValid ? '' : validation);
            }
        }, 150);
    }, [inputValue, validateNonprofitName]);

    const handleFocus = useCallback(() => {
        // Clear validation error when user focuses back on the field
        setValidationError('');
        
        if (inputValue.length > 0 && filteredOptions.length > 0) {
            setShowDropdown(true);
        }
    }, [inputValue.length, filteredOptions.length]);

    return (
        <div className="form-group autocomplete-wrapper">
            <input
                type="text"
                className={`form-input ${validationError ? 'error' : ''}`}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                onFocus={handleFocus}
                placeholder={question.PlaceholderText || "Enter organization name"}
            />
            {validationError && (
                <span className="error-message">
                    {validationError.message}
                    {validationError.emailLink && (
                        <a
                            href={`mailto:${validationError.emailLink}`}
                            className="error-email-link"
                            style={{ color: '#e74c3c', textDecoration: 'underline' }}
                        >
                            {validationError.emailLink}
                        </a>
                    )}
                    {validationError.messageEnd}
                </span>
            )}
            {showDropdown && filteredOptions.length > 0 && (
                <div className="autocomplete-dropdown">
                    {filteredOptions.map((option, index) => (
                        <div
                            key={option}
                            className={`autocomplete-option ${index === highlightedIndex ? 'highlighted' : ''}`}
                            onClick={() => handleOptionClick(option)}
                            onMouseEnter={() => setHighlightedIndex(index)}
                        >
                            {option}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
});

const RegistrationForm = () => {
    const { eventNameId } = useParams();
    const navigate = useNavigate();
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [answers, setAnswers] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [userEmail, setUserEmail] = useState('');
    const [event, setEvent] = useState(null);
    const [eventRegistrationId, setEventRegistrationId] = useState(null);
    const [eventEntityId, setEventEntityId] = useState(null);
    const [nonprofitData, setNonprofitData] = useState([]);
    // Whether the organization name must match the approved/invited nonprofit list for this registration type
    const [nonprofitGated, setNonprofitGated] = useState(false);
    // 'pending' until we know whether this registration type is gated; 'error' blocks submit with a retry
    const [gatingStatus, setGatingStatus] = useState('pending');

    useEffect(() => {
        // Get EventRegistrationGuid and email from localStorage
        const storedGuid = localStorage.getItem('eventRegistrationGuid');
        const storedEmail = localStorage.getItem('userEmail');
        if (!storedGuid) {
            // If no GUID, redirect back to email registration
            navigate(`/event/${eventNameId}/registration`);
            return;
        }
        setUserEmail(storedEmail || '');

        const fetchData = async () => {
            try {
                // Fetch event details, registration details, and questions
                const [eventResponse, registrationResponse, questionsResponse] = await Promise.all([
                    axios.get(`${API_BASE_URL}/public/events/${eventNameId}`),
                    axios.get(`${API_BASE_URL}/public/event-registrations/guid/${storedGuid}`),
                    axios.get(`${API_BASE_URL}/public/event-questions/${storedGuid}`)
                ]);

                setEvent(eventResponse.data);
                setEventRegistrationId(registrationResponse.data.EventRegistrationId);
                setEventEntityId(registrationResponse.data.EventEntityId);
                setQuestions(questionsResponse.data);

            } catch (err) {
                setError('Failed to load registration data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [eventNameId, navigate]);

    // Approved nonprofit list. Events using the invite model (2027+) serve the list from the API;
    // earlier events (Non-Profit EventEntityId 1) use the static Nonprofit.json list.
    const loadNonprofitData = useCallback(async () => {
        if (!eventEntityId || !eventNameId) return;
        setGatingStatus('pending');
        let apiFailed = false;
        try {
            const response = await axios.get(`${API_BASE_URL}/donor-circle/public/approved-nonprofits/${eventNameId}`);
            const { gated, nonprofit_event_entity_id: nonprofitEntityId, names } = response.data;
            if (gated && eventEntityId === nonprofitEntityId) {
                setNonprofitGated(true);
                setNonprofitData(names || []);
                setGatingStatus('ready');
                return;
            }
            if (eventEntityId !== 1) {
                // The API confirmed this registration type is not gated
                setNonprofitGated(false);
                setGatingStatus('ready');
                return;
            }
        } catch (err) {
            console.warn('Could not load approved nonprofit list from API:', err);
            apiFailed = true;
        }
        if (eventEntityId === 1) {
            // Legacy Non-Profit type: static list fallback
            try {
                const response = await fetch('/Nonprofit.json');
                const data = await response.json();
                setNonprofitGated(true);
                setNonprofitData(data);
                setGatingStatus('ready');
            } catch (err) {
                console.error('Failed to load nonprofit data:', err);
                setGatingStatus('error');
            }
            return;
        }
        // Unknown whether this type is gated because the request failed: do not allow submission
        setNonprofitGated(false);
        setGatingStatus(apiFailed ? 'error' : 'ready');
    }, [eventEntityId, eventNameId]);

    useEffect(() => { loadNonprofitData(); }, [loadNonprofitData]);

    // Invited nonprofits: prefill the organization name from the invitation
    useEffect(() => {
        const invite = getStoredInvite();
        if (!invite || invite.event_name_id !== eventNameId || !invite.nominee_name || questions.length === 0) return;
        const companyQuestion = questions.find((q) => q.DataName && q.DataName.toLowerCase() === 'companyname');
        if (!companyQuestion) return;
        setAnswers((prev) => (prev[companyQuestion.EventQuestionId]?.value
            ? prev
            : { ...prev, [companyQuestion.EventQuestionId]: { value: invite.nominee_name } }));
    }, [questions, eventNameId]);

    // Handle clicking outside multiselect dropdowns to close them
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.multiselect-wrapper')) {
                // Close all open multiselect dropdowns
                const openDropdowns = document.querySelectorAll('.multiselect-options.open');
                openDropdowns.forEach(dropdown => {
                    dropdown.classList.remove('open');
                });
            }
        };

        const handleScroll = (event) => {
            // If scrolling within a dropdown, prevent page scroll
            if (event.target.closest('.multiselect-options')) {
                event.stopPropagation();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('wheel', handleScroll, { passive: false });
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('wheel', handleScroll);
        };
    }, []);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    const handleAnswerChange = useCallback((questionId, answerId, value) => {
        setAnswers(prev => ({
            ...prev,
            [questionId]: {
                answerId: answerId,
                value: value
            }
        }));
    }, []);

    const handleTextAnswerChange = useCallback((questionId, value) => {
        setAnswers(prev => ({
            ...prev,
            [questionId]: {
                value: value
            }
        }));
    }, []);

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validateNonprofitName = (companyName) => {
        if (!companyName || companyName.trim() === '') {
            return { isValid: false, message: 'Organization name is required' };
        }

        const trimmedName = companyName.trim();
        const isInList = nonprofitData.some(org =>
            org.toLowerCase() === trimmedName.toLowerCase()
        );

        if (!isInList) {
            return {
                isValid: false,
                message: 'This organization is not currently in our list of approved nonprofits. Please contact ',
                emailLink: 'service@funds4charity.org',
                messageEnd: ' for assistance.'
            };
        }

        return { isValid: true, message: '' };
    };

    const validateAnswers = () => {
        const errors = [];

        questions.forEach(question => {
            const answer = answers[question.EventQuestionId];

            if (question.IsRequired && !answer) {
                errors.push(`Question "${question.QuestionName || question.QuestionText}" is required`);
            }

            // Validate email format if it's an email question
            if (answer && answer.value) {
                if (question.QuestionTypeName && question.QuestionTypeName.toLowerCase().includes('email')) {
                    if (!validateEmail(answer.value)) {
                        errors.push(`Please enter a valid email address for "${question.QuestionName || question.QuestionText}"`);
                    }
                }

                // Validate nonprofit name for EventEntityId = 1 and CompanyName field
                if (nonprofitGated && question.DataName &&
                    question.DataName.toLowerCase() === 'companyname') {
                    const validation = validateNonprofitName(answer.value);
                    if (!validation.isValid) {
                        errors.push(validation.message + validation.emailLink + validation.messageEnd);
                    }
                }
            }
        });

        return errors;
    };

    const isFormValid = () => {
        // Never allow submission before we know whether the organization name must match the approved list
        if (gatingStatus !== 'ready') return false;

        // Check if all required questions have answers
        const requiredQuestions = questions.filter(q => q.IsRequired);

        for (const question of requiredQuestions) {
            const answer = answers[question.EventQuestionId];

            // For different question types, check if they have valid answers
            if (question.QuestionTypeId === 4) { // Checkbox - multiple selections
                if (!answer || !answer.selectedAnswers || answer.selectedAnswers.length === 0) {
                    return false;
                }
            } else if (question.QuestionTypeId === 3 || question.QuestionTypeId === 5 || question.QuestionTypeId === 15) { // Radio, Select, Yes_No
                if (!answer || (!answer.answerId && !answer.value)) {
                    return false;
                }
            } else { // All other input types
                if (!answer || !answer.value || answer.value.trim() === '') {
                    return false;
                }
            }
        }

        // Additional validation for nonprofit names (approved / invited list)
        if (nonprofitGated) {
            const validationErrors = validateAnswers();
            if (validationErrors.length > 0) {
                return false;
            }
        }

        return true;
    };

    const handleSubmit = async () => {
        const validationErrors = validateAnswers();
        if (validationErrors.length > 0) {
            setError(validationErrors.join(', '));
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            if (!eventRegistrationId) {
                throw new Error('Registration session expired. Please start over.');
            }

            // Prepare answers for submission
            const answersToSubmit = [];

            Object.keys(answers).forEach(questionId => {
                const answer = answers[questionId];
                const question = questions.find(q => q.EventQuestionId === parseInt(questionId));

                if (!question) return;

                // Handle different question types
                if (question.QuestionTypeId === 4) { // Multi-select dropdown
                    if (answer.selectedAnswers && answer.selectedAnswers.length > 0) {
                        answer.selectedAnswers.forEach(answerId => {
                            // Find the corresponding answer text
                            const answerOption = question.answers.find(a => a.EventQuestionAnswerId === answerId);
                            const answerText = answerOption ? answerOption.AnswerText : null;

                            answersToSubmit.push({
                                EventRegistrationId: eventRegistrationId,
                                EventQuestionId: parseInt(questionId),
                                EventQuestionAnswerId: parseInt(answerId),
                                AnswerText: answerText
                            });
                        });
                    }
                } else if (question.QuestionTypeId === 3 || question.QuestionTypeId === 5 || question.QuestionTypeId === 15) { // Radio, Select, Yes_No
                    if (answer.answerId) {
                        // Find the corresponding answer text
                        const answerOption = question.answers.find(a => a.EventQuestionAnswerId === parseInt(answer.answerId));
                        const answerText = answerOption ? answerOption.AnswerText : null;

                        answersToSubmit.push({
                            EventRegistrationId: eventRegistrationId,
                            EventQuestionId: parseInt(questionId),
                            EventQuestionAnswerId: parseInt(answer.answerId),
                            AnswerText: answerText
                        });
                    } else if (answer.value) {
                        // For Yes_No questions that use value instead of answerId
                        answersToSubmit.push({
                            EventRegistrationId: eventRegistrationId,
                            EventQuestionId: parseInt(questionId),
                            EventQuestionAnswerId: null,
                            AnswerText: answer.value
                        });
                    }
                } else { // All other input types (text, email, phone, etc.)
                    if (answer.value) {
                        answersToSubmit.push({
                            EventRegistrationId: eventRegistrationId,
                            EventQuestionId: parseInt(questionId),
                            EventQuestionAnswerId: null,
                            AnswerText: answer.value
                        });
                    }
                }
            });

            // Submit all answers using the completion endpoint
            await axios.post(`${API_BASE_URL}/public/event-registration-answers/complete-registration`, {
                registration_id: eventRegistrationId,
                answers: answersToSubmit
            });

            // Clear localStorage and navigate to success
            localStorage.removeItem('eventRegistrationGuid');
            clearStoredInvite();
            localStorage.removeItem('userEmail');
            navigate(`/event/${eventNameId}/registration/success`);

        } catch (err) {
            console.error('Submission error:', err);
            if (err.response?.data?.detail) {
                setError(`Failed to submit registration: ${err.response.data.detail}`);
            } else if (err.message) {
                setError(err.message);
            } else {
                setError('Failed to submit registration. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };


    const renderQuestion = (question) => {
        const questionAnswer = answers[question.EventQuestionId];

        switch (question.QuestionTypeId) {
            case 1: // Input - Single Line Text Input
                // Check if this is CompanyName field for EventEntityId = 1
                const isCompanyNameField = nonprofitGated &&
                    question.DataName &&
                    question.DataName.toLowerCase() === 'companyname';


                if (isCompanyNameField) {
                    return (
                        <AutocompleteInput
                            question={question}
                            questionAnswer={questionAnswer}
                            onValueChange={handleTextAnswerChange}
                            nonprofitData={nonprofitData}
                        />
                    );
                }

                return (
                    <div className="form-group">
                        <input
                            type="text"
                            className="form-input"
                            value={questionAnswer?.value || ''}
                            onChange={(e) => handleTextAnswerChange(question.EventQuestionId, e.target.value)}
                            placeholder={question.PlaceholderText || "Enter your answer"}
                        />
                    </div>
                );

            case 2: // TextArea - Multi-line text input (minimum 3 lines)
                return (
                    <div className="form-group">
                        <textarea
                            className="form-textarea"
                            value={questionAnswer?.value || ''}
                            onChange={(e) => handleTextAnswerChange(question.EventQuestionId, e.target.value)}
                            placeholder={question.PlaceholderText || "Enter your answer"}
                            rows={3}
                        />
                    </div>
                );

            case 3: // Radio - Radio button options
                return (
                    <div className="form-group">
                        <div className="radio-group">
                            {question.answers && question.answers.map((answer) => (
                                <label key={answer.EventQuestionAnswerId} className="radio-option">
                                    <input
                                        type="radio"
                                        name={`question_${question.EventQuestionId}`}
                                        value={answer.EventQuestionAnswerId}
                                        checked={questionAnswer?.answerId === answer.EventQuestionAnswerId}
                                        onChange={(e) => handleAnswerChange(question.EventQuestionId, answer.EventQuestionAnswerId, e.target.value)}
                                    />
                                    <span className="radio-text">{answer.AnswerText}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                );

            case 4: // Multi-select dropdown
                return (
                    <div className="form-group">
                        <div className="multiselect-wrapper">
                            <div
                                className="multiselect-dropdown"
                                onClick={(e) => {
                                    const dropdown = document.getElementById(`multiselect-${question.EventQuestionId}`);
                                    if (dropdown) {
                                        const isOpen = dropdown.classList.contains('open');

                                        // Close all other dropdowns first
                                        const allDropdowns = document.querySelectorAll('.multiselect-options.open');
                                        allDropdowns.forEach(d => d.classList.remove('open'));

                                        if (!isOpen) {
                                            dropdown.classList.add('open');

                                            // Position the dropdown
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            const viewportHeight = window.innerHeight;
                                            const dropdownHeight = Math.min(200, question.answers.length * 48 + 16); // Approximate height

                                            // Check if dropdown would go below viewport
                                            if (rect.bottom + dropdownHeight > viewportHeight) {
                                                dropdown.classList.add('dropdown-up');
                                            } else {
                                                dropdown.classList.remove('dropdown-up');
                                            }
                                        }
                                    }
                                }}
                            >
                                <div className="multiselect-display">
                                    {questionAnswer?.selectedAnswers && questionAnswer.selectedAnswers.length > 0 ? (
                                        <span className="selected-count">
                                            {questionAnswer.selectedAnswers.length} item{questionAnswer.selectedAnswers.length !== 1 ? 's' : ''} selected
                                        </span>
                                    ) : (
                                        <span className="placeholder">Select multiple options</span>
                                    )}
                                </div>
                                <i className="fas fa-chevron-down multiselect-icon"></i>
                            </div>
                            <div
                                id={`multiselect-${question.EventQuestionId}`}
                                className="multiselect-options"
                            >
                                {question.answers && question.answers.map((answer) => (
                                    <label key={answer.EventQuestionAnswerId} className="multiselect-option">
                                        <input
                                            type="checkbox"
                                            value={answer.EventQuestionAnswerId}
                                            checked={questionAnswer?.selectedAnswers?.includes(answer.EventQuestionAnswerId) || false}
                                            onChange={(e) => {
                                                const currentAnswers = questionAnswer?.selectedAnswers || [];
                                                const newAnswers = e.target.checked
                                                    ? [...currentAnswers, answer.EventQuestionAnswerId]
                                                    : currentAnswers.filter(id => id !== answer.EventQuestionAnswerId);
                                                setAnswers(prev => ({
                                                    ...prev,
                                                    [question.EventQuestionId]: {
                                                        selectedAnswers: newAnswers
                                                    }
                                                }));
                                            }}
                                        />
                                        <span className="multiselect-text">{answer.AnswerText}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                );

            case 5: // Select - Dropdown select
                return (
                    <div className="form-group">
                        <div className="select-wrapper">
                            <select
                                value={questionAnswer?.answerId || ''}
                                onChange={(e) => handleAnswerChange(question.EventQuestionId, e.target.value, e.target.value)}
                                className="form-select"
                            >
                                <option value="">Select an option</option>
                                {question.answers && question.answers.map((answer) => (
                                    <option key={answer.EventQuestionAnswerId} value={answer.EventQuestionAnswerId}>
                                        {answer.AnswerText}
                                    </option>
                                ))}
                            </select>
                            <i className="fas fa-chevron-down select-icon"></i>
                        </div>
                    </div>
                );

            case 6: // DatePicker - Date input
                return (
                    <div className="form-group">
                        <input
                            type="date"
                            className="form-input"
                            value={questionAnswer?.value || ''}
                            onChange={(e) => handleTextAnswerChange(question.EventQuestionId, e.target.value)}
                        />
                    </div>
                );

            case 7: // Upload - File upload
                return (
                    <div className="form-group">
                        <input
                            type="file"
                            className="form-input"
                            onChange={(e) => handleTextAnswerChange(question.EventQuestionId, e.target.files[0]?.name || '')}
                        />
                    </div>
                );

            case 8: // Email - Email input with validation
                // Check if this is the registrant's own email question (not assistant email or other)
                const isRegistrantEmail = question.DataName &&
                    (question.DataName.toLowerCase().includes('email') &&
                        !question.DataName.toLowerCase().includes('assistant'));

                return (
                    <div className="form-group">
                        <input
                            type="email"
                            className="form-input"
                            value={questionAnswer?.value || (isRegistrantEmail ? userEmail : '')}
                            onChange={(e) => handleTextAnswerChange(question.EventQuestionId, e.target.value)}
                            placeholder={question.PlaceholderText || "Enter your email address"}
                            disabled={isRegistrantEmail && userEmail && !questionAnswer?.value}
                        />
                    </div>
                );

            case 9: // Url - URL input
                return (
                    <div className="form-group">
                        <input
                            type="url"
                            className="form-input"
                            value={questionAnswer?.value || ''}
                            onChange={(e) => handleTextAnswerChange(question.EventQuestionId, e.target.value)}
                            placeholder={question.PlaceholderText || "Enter URL (e.g., https://example.com)"}
                        />
                    </div>
                );

            case 10: // Label - Display only (no input)
                return (
                    <div className="form-group">
                        <div className="label-display">
                            {question.QuestionName || question.QuestionText}
                        </div>
                    </div>
                );

            case 11: // HTML - Display HTML content (no input)
                return (
                    <div className="form-group">
                        <div
                            className="html-display"
                            dangerouslySetInnerHTML={{ __html: question.QuestionName || question.QuestionText }}
                        />
                    </div>
                );

            case 12: // Number - Number input
                return (
                    <div className="form-group">
                        <input
                            type="number"
                            className="form-input"
                            value={questionAnswer?.value || ''}
                            onChange={(e) => handleTextAnswerChange(question.EventQuestionId, e.target.value)}
                            placeholder={question.PlaceholderText || "Enter a number"}
                        />
                    </div>
                );

            case 13: // Hidden - Hidden input (not displayed)
                return null;

            case 14: // PhoneNumber - Phone input with country selector
                return (
                    <div className="form-group">
                        <PhoneInput
                            value={questionAnswer?.value || ''}
                            onChange={(value) => handleTextAnswerChange(question.EventQuestionId, value)}
                            placeholder={question.PlaceholderText || "Enter phone number"}
                            className="phone-input"
                            defaultCountry="US"
                        />
                    </div>
                );

            case 15: // Yes_No - Yes/No radio buttons
                return (
                    <div className="form-group">
                        <div className="radio-group">
                            <label className="radio-option">
                                <input
                                    type="radio"
                                    name={`question_${question.EventQuestionId}`}
                                    value="yes"
                                    checked={questionAnswer?.value === 'yes'}
                                    onChange={(e) => handleTextAnswerChange(question.EventQuestionId, e.target.value)}
                                />
                                <span className="radio-text">Yes</span>
                            </label>
                            <label className="radio-option">
                                <input
                                    type="radio"
                                    name={`question_${question.EventQuestionId}`}
                                    value="no"
                                    checked={questionAnswer?.value === 'no'}
                                    onChange={(e) => handleTextAnswerChange(question.EventQuestionId, e.target.value)}
                                />
                                <span className="radio-text">No</span>
                            </label>
                        </div>
                    </div>
                );

            default:
                // Fallback to text input for unknown types
                return (
                    <div className="form-group">
                        <input
                            type="text"
                            className="form-input"
                            value={questionAnswer?.value || ''}
                            onChange={(e) => handleTextAnswerChange(question.EventQuestionId, e.target.value)}
                            placeholder={question.PlaceholderText || "Enter your answer"}
                        />
                    </div>
                );
        }
    };

    if (loading) {
        return <div className="loading">Loading registration form...</div>;
    }

    if (error || !event) {
        return <div className="error">{error || 'Event not found'}</div>;
    }

    if (questions.length === 0) {
        return (
            <div className="registration-form">
                {/* Event Banner with Logo */}
                <div className="event-banner">
                    {event.EventImage && (
                        <img src={event.EventImage} alt="Event Logo" className="event-logo" />
                    )}
                </div>

                {/* Event Title */}
                <div className="event-header">
                    <h1 className="event-title">{event.Name}</h1>
                </div>

                {/* Event Details */}
                <div className="event-details">
                    <div className="detail-item location-item">
                        <span className="detail-text">
                            <i className="fas fa-map-marker-alt detail-icon"></i>
                            {event.Location}
                            {event.Address1 && ` - ${event.Address1}`}
                            {event.City && `, ${event.City}`}
                            {event.StateProvince && `, ${event.StateProvince}`}
                            {event.Country && `, ${event.Country}`}
                        </span>
                    </div>

                    <div className="detail-item date-time-row">
                        <div className="date-section">
                            <i className="fas fa-calendar-alt detail-icon"></i>
                            <span className="detail-text">
                                {formatDate(event.StartDate)} - {formatDate(event.EndDate)}
                            </span>

                            <i className="fas fa-clock detail-icon"></i>
                            <span className="detail-text">
                                {formatTime(event.StartDate)} - {formatTime(event.EndDate)} {event.TimeZone || 'EDT'}
                            </span>
                        </div>

                    </div>
                </div>

                <div className="separator"></div>

                <div className="no-questions">
                    <h2>No Questions Required</h2>
                    <p>There are no additional questions for your registration type.</p>
                    <button
                        className="next-button active"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Submitting...' : 'Complete Registration'}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="registration-form">
            {/* Event Banner with Logo */}
            <div className="event-banner">
                {event.EventImage && (
                    <img src={event.EventImage} alt="Event Logo" className="event-logo" />
                )}
            </div>

            {/* Event Title */}
            <div className="event-header">
                <h1 className="event-title">{event.Name}</h1>
            </div>

            {/* Event Details */}
            <div className="event-details">
                <div className="detail-item location-item">
                    <span className="detail-text">
                        <i className="fas fa-map-marker-alt detail-icon"></i>
                        {event.Location}
                        {event.Address1 && ` - ${event.Address1}`}
                        {event.City && `, ${event.City}`}
                        {event.StateProvince && `, ${event.StateProvince}`}
                        {event.Country && `, ${event.Country}`}
                    </span>
                </div>

                <div className="detail-item date-time-row">
                    <div className="date-section">
                        <i className="fas fa-calendar-alt detail-icon"></i>
                        <span className="detail-text">
                            {formatDate(event.StartDate)} - {formatDate(event.EndDate)}
                        </span>

                        <i className="fas fa-clock detail-icon"></i>
                        <span className="detail-text">
                            {formatTime(event.StartDate)} - {formatTime(event.EndDate)} {event.TimeZone || 'EDT'}
                        </span>
                    </div>
                </div>
            </div>

            <div className="separator"></div>

            <div className="questions-container">
                {questions.map((question) => (
                    <div key={question.EventQuestionId} className="question-section">
                        <h3 className="question-prompt">
                            {question.QuestionName || question.QuestionText}
                            {question.IsRequired && <span className="required">*</span>}
                        </h3>
                        {question.QuestionDescription && (
                            <p className="question-description">
                                {question.QuestionDescription}
                            </p>
                        )}
                        {renderQuestion(question)}
                    </div>
                ))}
            </div>

            {gatingStatus === 'error' && (
                <div className="error-message" role="alert">
                    We couldn&rsquo;t load the registration requirements for this organization type.{' '}
                    <button type="button" className="link-button" onClick={loadNonprofitData}>Try again</button>
                </div>
            )}

            <div className="navigation-buttons">
                <button
                    type="button"
                    className={`next-button ${isFormValid() ? 'active' : ''}`}
                    onClick={handleSubmit}
                    disabled={isSubmitting || !isFormValid()}
                >
                    {isSubmitting ? 'Submitting...' : (gatingStatus === 'pending' ? 'Loading…' : 'Submit Registration')}
                </button>
            </div>
        </div>
    );
};

export default RegistrationForm;
